/**
 * Promote bare YouTube/Vimeo URLs in update HTML into responsive iframes at
 * render time. Defensive against scenarios where the editor saved a plain URL
 * (instead of an iframe block) or where the iframe was lost somewhere
 * upstream — see PD-34534.
 *
 * Rules:
 *   - Only matches https URLs on YouTube (any variant) and Vimeo.
 *   - Skips URLs inside an existing <iframe> element (whether as `src` or
 *     text), so we never double-embed.
 *   - Skips URLs inside an existing <a href="...">...</a>, so we never
 *     break authored links. Issuers who want both an embed AND a link should
 *     paste the URL twice (once bare, once as a link).
 *   - When a URL is bare text (in a paragraph, list item, etc.), it is
 *     replaced in-place with the iframe.
 *   - Pure string transformation — no DOM, no React, safe to call during SSR.
 */

const YOUTUBE_REGEX =
  /https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[?&][^\s"'<>]*)?/g;

const VIMEO_REGEX =
  /https?:\/\/(?:www\.)?(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)(?:[?#][^\s"'<>]*)?/g;

const IFRAME_BLOCK_REGEX = /<iframe[\s\S]*?<\/iframe>/gi;
const ANCHOR_BLOCK_REGEX = /<a\b[\s\S]*?<\/a>/gi;

const buildIframe = (embedUrl) =>
  `<iframe src="${embedUrl}" width="100%" height="480" frameborder="0" ` +
  `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" ` +
  `allowfullscreen style="width: 100%; aspect-ratio: 16/9; border: 0;" ` +
  `data-autoembedded="1"></iframe>`;

const youtubeEmbedUrl = (id) => `https://www.youtube.com/embed/${id}`;
const vimeoEmbedUrl = (id) => `https://player.vimeo.com/video/${id}`;

const collectRanges = (html, regex) => {
  const ranges = [];
  const re = new RegExp(regex.source, regex.flags);
  let m = re.exec(html);
  while (m !== null) {
    ranges.push([m.index, m.index + m[0].length]);
    m = re.exec(html);
  }
  return ranges;
};

const replaceOutsideRanges = (html, regex, ranges, transform) => {
  if (!html || typeof html !== 'string') return html;
  const isInside = (offset) => ranges.some(([start, end]) => offset >= start && offset < end);
  const re = new RegExp(regex.source, regex.flags);
  return html.replace(re, (match, ...args) => {
    const offset = args[args.length - 2];
    if (isInside(offset)) return match;
    return transform(match, ...args);
  });
};

export function autoEmbedVideos(html) {
  if (!html || typeof html !== 'string') return html;

  const skipRanges = [
    ...collectRanges(html, IFRAME_BLOCK_REGEX),
    ...collectRanges(html, ANCHOR_BLOCK_REGEX),
  ];

  let result = html;
  result = replaceOutsideRanges(result, YOUTUBE_REGEX, skipRanges, (_match, id) =>
    buildIframe(youtubeEmbedUrl(id)),
  );

  const skipRangesAfterYouTube = [
    ...collectRanges(result, IFRAME_BLOCK_REGEX),
    ...collectRanges(result, ANCHOR_BLOCK_REGEX),
  ];
  result = replaceOutsideRanges(result, VIMEO_REGEX, skipRangesAfterYouTube, (_match, id) =>
    buildIframe(vimeoEmbedUrl(id)),
  );

  return result;
}

export const __TEST_ONLY__ = {
  YOUTUBE_REGEX,
  VIMEO_REGEX,
  buildIframe,
  youtubeEmbedUrl,
  vimeoEmbedUrl,
};
